import { useMemo } from "react";
import { Chart } from "primereact/chart";

import type { HomeChartPoint } from "@/modules/home";
import { resolveHomeColor } from "./chart-colors";
import styles from "./home-chart.module.scss";

interface HomeChartProps {
  points: HomeChartPoint[];
  /** Nome do gráfico para leitores de tela e legenda da tabela equivalente. */
  ariaLabel: string;
  /** Cabeçalho da coluna de rótulos na tabela equivalente (ex.: "Ano"). */
  labelHeader: string;
  /** Cabeçalho da coluna de valores na tabela equivalente. */
  valueHeader: string;
  formatValue: (value: number) => string;
  /**
   * Escreve o valor de cada ponto sobre a curva, como no cartão Capex. O
   * protótipo omite a unidade aí, que já aparece no total do cabeçalho.
   */
  formatPointLabel?: (value: number) => string;
  /** Escreve o valor de cada ponto sobre a curva, como no cartão Capex. */
  showPointLabels?: boolean;
  /** Mostra os rótulos do eixo horizontal, como nos anos do cartão Capex. */
  showAxisLabels?: boolean;
  /**
   * Enquadramento vertical da curva. `"zero"` ancora o eixo em zero, como na
   * curva acumulada do Financeiro; `"relevo"` aproxima a faixa de valores para
   * dar à variação o mesmo relevo do cartão Capex do protótipo.
   */
  baseline?: "zero" | "relevo";
  /**
   * Espessura da linha da série. O Financeiro do protótipo mostra apenas a área
   * preenchida, sem contorno, e por isso a zera.
   */
  borderWidth?: number;
  /** Índice do ponto marcado com "Hoje" no cartão Financeiro. */
  markerIndex?: number;
  /** Mostra as linhas internas da grade do gráfico. */
  showGrid?: boolean;
  className?: string;
  testId: string;
}

/**
 * Curva de área dos cartões Capex e Financeiro, construída sobre o componente
 * `Chart` do PrimeReact — nenhuma outra biblioteca de gráficos é usada no
 * projeto (AC5).
 *
 * Os pontos vêm sempre por parâmetro, da camada de consulta da Home, e nunca
 * embutidos aqui (AC6). O canvas é escondido dos leitores de tela e os mesmos
 * valores são publicados em uma tabela equivalente, o que mantém os indicadores
 * legíveis quando o gráfico não pode ser interpretado.
 */
export function HomeChart({
  points,
  ariaLabel,
  labelHeader,
  valueHeader,
  formatValue,
  formatPointLabel = formatValue,
  showPointLabels = false,
  showAxisLabels = false,
  baseline = "zero",
  borderWidth = 3,
  markerIndex,
  showGrid = true,
  className,
  testId,
}: HomeChartProps) {
  const data = useMemo(() => {
    const line = resolveHomeColor("--home-chart-line");

    return {
      labels: points.map((point) => point.label),
      datasets: [
        {
          data: points.map((point) => point.value),
          borderColor: line,
          borderWidth,
          // A área sob a curva é o que dá volume ao gráfico do protótipo; o véu
          // translúcido mantém a fotografia visível ao fundo do cartão.
          backgroundColor: resolveHomeColor("--home-chart-fill"),
          fill: "origin",
          // O protótipo liga os pontos em segmentos retos, sem suavização.
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: line,
        },
      ],
    };
  }, [borderWidth, points]);

  const options = useMemo(() => {
    const axisColor = resolveHomeColor("--home-text");
    const gridColor = resolveHomeColor("--home-chart-grid");
    const values = points.map((point) => point.value);
    const scale = resolveVerticalScale(values, baseline);

    return {
      responsive: true,
      maintainAspectRatio: false,
      // Espaço no topo e nas laterais para os valores escritos sobre a curva
      // não serem cortados nos pontos das pontas.
      layout: {
        padding: showPointLabels ? { top: 22, left: 20, right: 20 } : { top: 12 },
      },
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (item: { parsed: { y: number } }) => formatValue(item.parsed.y),
          },
        },
      },
      scales: {
        x: {
          // O protótipo marca cada rótulo com uma linha vertical tênue.
          grid: { display: false },
          border: { display: false },
          ticks: { display: showAxisLabels, color: axisColor, font: { size: 11 } },
        },
        y: {
          min: scale.min,
          max: scale.max,
          grid: { display: false },
          border: { display: false },
          ticks: { display: false },
        },
      },
    };
  }, [baseline, formatValue, points, showAxisLabels, showPointLabels]);

  const plugins = useMemo(
    () => [
      createOverlayPlugin({
        points,
        formatPointLabel,
        showPointLabels,
        markerIndex,
        gridColor: resolveHomeColor("--home-chart-grid"),
        showAxisLabels,
        showGrid,
      }),
    ],
    [formatPointLabel, markerIndex, points, showAxisLabels, showGrid, showPointLabels],
  );

  return (
    <figure className={[styles.figure, className].filter(Boolean).join(" ")} data-testid={testId}>
      <div className={styles.canvas} aria-hidden>
        <Chart type="line" data={data} options={options} plugins={plugins} />
      </div>

      <table className={styles.srOnly} data-testid={`${testId}-dados`}>
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th scope="col">{labelHeader}</th>
            <th scope="col">{valueHeader}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.label}>
              <th scope="row">{point.label}</th>
              <td>{formatValue(point.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/**
 * Faixa vertical do gráfico.
 *
 * Em `"relevo"` o eixo não parte de zero: a faixa é encostada nos valores da
 * série — com folga maior embaixo do que em cima — para que a variação entre os
 * anos se veja como no protótipo, em vez de virar uma linha quase reta no topo
 * de um eixo dominado pela distância até zero.
 */
function resolveVerticalScale(
  values: number[],
  baseline: "zero" | "relevo",
): { min: number; max: number } {
  const maior = Math.max(...values, 0);
  if (baseline === "zero") return { min: 0, max: maior * 1.25 };

  const menor = Math.min(...values);
  const amplitude = maior - menor;
  // Uma série constante não tem amplitude para trabalhar: cai no enquadramento
  // pelo próprio valor, que ainda deixa a linha visível no meio do cartão.
  const folga = amplitude || Math.abs(maior) || 1;

  return { min: menor - folga * 0.45, max: maior + folga * 0.25 };
}

interface OverlayPluginParams {
  points: HomeChartPoint[];
  formatPointLabel: (value: number) => string;
  showPointLabels: boolean;
  markerIndex?: number;
  gridColor: string;
  showAxisLabels: boolean;
  showGrid: boolean;
}

/**
 * Os dois detalhes do protótipo que o Chart.js não desenha por conta própria: o
 * valor escrito acima de cada ponto do Capex e a linha vertical "Hoje" do
 * Financeiro. Ficam em um plugin local em vez de uma dependência a mais.
 */
function createOverlayPlugin({
  points,
  formatPointLabel,
  showPointLabels,
  markerIndex,
  gridColor,
  showAxisLabels,
  showGrid,
}: OverlayPluginParams) {
  return {
    id: "home-chart-overlay",
    afterDatasetsDraw(chart: {
      ctx: CanvasRenderingContext2D;
      width: number;
      chartArea: { top: number; right: number; bottom: number; left: number };
      scales: {
        x: { ticks: unknown[]; getPixelForTick: (index: number) => number };
        y: { ticks: unknown[]; getPixelForTick: (index: number) => number };
      };
      getDatasetMeta: (index: number) => { data: { x: number; y: number }[] };
    }) {
      const meta = chart.getDatasetMeta(0);
      const { ctx, chartArea } = chart;
      if (!ctx || !meta?.data?.length) return;

      ctx.save();

      if (showGrid) {
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();

        if (showAxisLabels) {
          chart.scales.x.ticks.forEach((_, index) => {
            const x = chart.scales.x.getPixelForTick(index);
            if (x > chartArea.left && x < chartArea.right) {
              ctx.moveTo(x, chartArea.top);
              ctx.lineTo(x, chartArea.bottom);
            }
          });
        }

        chart.scales.y.ticks.forEach((_, index) => {
          const y = chart.scales.y.getPixelForTick(index);
          if (y > chartArea.top && y < chartArea.bottom) {
            ctx.moveTo(chartArea.left, y);
            ctx.lineTo(chartArea.right, y);
          }
        });

        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (showPointLabels) {
        ctx.fillStyle = resolveHomeColor("--home-text");
        ctx.font = "600 11px Inter, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";

        meta.data.forEach((element, index) => {
          const point = points[index];
          if (!point) return;

          const text = formatPointLabel(point.value);
          // Os pontos das pontas ficam sobre a borda do canvas: o texto é
          // trazido para dentro para não sair cortado.
          const half = ctx.measureText(text).width / 2 + 2;
          const x = Math.min(Math.max(element.x, half), chart.width - half);

          ctx.fillText(text, x, element.y - 8);
        });
      }

      const marker = markerIndex === undefined ? undefined : meta.data[markerIndex];
      if (marker) {
        ctx.strokeStyle = resolveHomeColor("--home-chart-marker");
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(marker.x, chartArea.top);
        ctx.lineTo(marker.x, chartArea.bottom);
        ctx.stroke();

        ctx.fillStyle = resolveHomeColor("--home-text");
        ctx.font = "500 10px Inter, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText("Hoje", marker.x, chartArea.top - 2);
      }

      ctx.restore();
    },
  };
}
